import { Day } from '../../../interfaces';
import {
  AzureGroup as AzureGroupApi,
  ClassBasicResponse,
  ClassResponse,
  CourseBasicResponse,
  CourseResponse,
  CourseTeacher,
  DayOfWeek,
  GradeBasicResponse,
  GradeResponse,
  GradeSettingsResponse,
  Person as ApiPerson,
} from '../../api/v2';
import {
  AzureGroup,
  ClassCourse,
  CourseOccurrence,
  Grade,
  GradeClass,
  GradeSettings,
  SignOffRequirement,
} from './classbookSettings.interfaces';

const defaultGradeSettings: GradeSettings = {
  gradeManagers: [],
  signOffRequirement: SignOffRequirement.none,
};

const defaultGradeProps = {
  settings: defaultGradeSettings,
  classes: [],
};

export const apiAzureGroupToAzureGroup = (
  azureGroup: AzureGroupApi | null | undefined,
): AzureGroup | undefined => {
  if (!azureGroup) {
    // eslint-disable-next-line unicorn/no-useless-undefined
    return undefined;
  }

  const { id, displayName, mailAddress } = azureGroup;
  return {
    id,
    name: displayName,
    mailAddress: mailAddress ?? '',
  };
};

export const classBasicResponseToGradeClass = (
  basicClass: ClassBasicResponse,
): GradeClass => {
  const { id, name, azureGroup } = basicClass;
  return {
    id: id ?? '',
    name: name ?? '',
    classTeachers: [],
    courses: [],
    signOffRequirement: SignOffRequirement.none,
    azureGroup: apiAzureGroupToAzureGroup(azureGroup),
  };
};

export const classResponseToGradeClass = (
  classResponse: ClassResponse,
): GradeClass => {
  const {
    id,
    name,
    azureGroup,
    signOffRequirement,
    classCourses,
    classTeachers,
  } = classResponse;

  return {
    id: id ?? '',
    name: name ?? '',
    classTeachers,
    courses: classCourses?.map(courseBasicResponseToClassCourse) ?? [],
    signOffRequirement:
      signOffRequirement === undefined
        ? SignOffRequirement.none
        : signOffRequirement,
    azureGroup: apiAzureGroupToAzureGroup(azureGroup),
  };
};

export const gradeBasicResponseToGrade = (grade: GradeBasicResponse): Grade => {
  const { id, azureGroup, name, gradeManagerIds } = grade;
  return {
    id: id ?? '',
    name: name ?? '',
    azureGroup: apiAzureGroupToAzureGroup(azureGroup),
    settings: {
      ...defaultGradeSettings,
      gradeManagerIds: gradeManagerIds || [],
    },
    classes: defaultGradeProps.classes,
  };
};

export const dayToDayOfWeek = (day: Day): DayOfWeek => {
  switch (day) {
    case Day.monday: {
      return DayOfWeek.monday;
    }
    case Day.tuesday: {
      return DayOfWeek.tuesday;
    }
    case Day.wednesday: {
      return DayOfWeek.wednesday;
    }
    case Day.thursday: {
      return DayOfWeek.thursday;
    }
    case Day.friday: {
      return DayOfWeek.friday;
    }
    case Day.saturday: {
      return DayOfWeek.saturday;
    }
    case Day.sunday: {
      return DayOfWeek.sunday;
    }
  }
};

export const gradeSettingsResponseToGradeSettings = (
  settings: GradeSettingsResponse | null | undefined,
): GradeSettings => {
  if (!settings) {
    return defaultGradeSettings;
  }

  const { gradeManagers, signOffRequirement } = settings;
  return {
    signOffRequirement: signOffRequirement
      ? (signOffRequirement as SignOffRequirement)
      : defaultGradeSettings.signOffRequirement,
    gradeManagers: gradeManagers ?? defaultGradeSettings.gradeManagers,
  };
};

export const gradeResponseToGrade = (gradeResponse: GradeResponse): Grade => {
  const { id, name, gradeClasses, azureGroup, settings } = gradeResponse;
  return {
    id: id ?? '',
    name: name ?? '',
    classes: gradeClasses?.map(classBasicResponseToGradeClass) ?? [],
    azureGroup: apiAzureGroupToAzureGroup(azureGroup),
    settings: gradeSettingsResponseToGradeSettings(settings),
  };
};

export const courseBasicResponseToClassCourse = (
  courseBasicResponse: CourseBasicResponse,
): ClassCourse => {
  const { id, name, color, azureGroup } = courseBasicResponse;
  return {
    id: id ?? '',
    name: name ?? '',
    courseTeachers: [],
    azureGroup: apiAzureGroupToAzureGroup(azureGroup),
    color: color === null ? undefined : color,
    occurrences: [],
    // #TODO: Fill with data!
    classes: [],
  };
};

export const courseResponseToClassCourse = (
  courseResponse: CourseResponse,
): ClassCourse => {
  const { id, name, courseTeachers, azureGroup, color, occurrences, classes } =
    courseResponse;

  return {
    id: id ?? '',
    name: name ?? '',
    courseTeachers: courseTeachers ?? [],
    azureGroup: apiAzureGroupToAzureGroup(azureGroup),
    color: color === null ? undefined : color,
    occurrences: occurrences?.map(
      o =>
        ({
          slot: {
            name: o.slot?.name ?? '',
            start: o.slot?.start ?? '',
            end: o.slot?.end ?? '',
            period: o.slot?.period,
          },
          weekday: o.weekday ? dayOfWeekToDay(o.weekday) : Day.monday,
        } as CourseOccurrence),
    ),
    classes,
  };
};

/**
 * Converts between the two enums DayOfWeek to Day
 */
export const dayOfWeekToDay = (day: DayOfWeek): Day => {
  const map = {
    [DayOfWeek.monday]: Day.monday,
    [DayOfWeek.tuesday]: Day.tuesday,
    [DayOfWeek.wednesday]: Day.wednesday,
    [DayOfWeek.thursday]: Day.thursday,
    [DayOfWeek.friday]: Day.friday,
    [DayOfWeek.saturday]: Day.saturday,
    [DayOfWeek.sunday]: Day.sunday,
  };

  return map[day];
};
